import React, { useMemo, useState } from 'react';
import { QRCode } from 'react-qr-code';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { useEventDetails } from '../hooks/useEventDetails';
import ConfirmModal from '../components/ConfirmModal';
import EventCancelledBanner from '../components/EventCancelledBanner';
import { showToast } from '../components/Toast';
import HostPicker from '../design-system/patterns/HostPicker';
import { MembershipChip } from '../components/MembershipChip';
import { isCurrentMember } from '../utils/membership';
import { DSCard, DSIcon, DSModal, DSButton, DSSheet, DSInput } from '../design-system/primitives';
import './EventManage.css';
import { personName } from '../utils/personName';

const TABS = [
  { key: 'attendees', label: 'Attendees' },
  { key: 'hosts', label: 'Hosts' },
  { key: 'updates', label: 'Updates' }
];

// The Square links a host shows at the door. Hosts have no cash drawer, so one of these is
// the only way a guest pays the game night rate rather than the full day pass.
//
// The URLs are the live ones. The design file carries a different Upgrade Pass link, which
// is placeholder text from the mock - pointing a real payment button at it would send
// money nowhere.
const PASSES = [
  {
    id: 'video_board_pass',
    name: 'Video/Board Game Only Pass',
    note: 'No pinball.',
    url: 'https://square.link/u/jx13yFLd'
  },
  {
    id: 'upgrade_pass',
    name: 'Upgrade Pass',
    note: 'Adds pinball for the rest of the night.',
    url: 'https://square.link/u/DzBCxdMF'
  }
];

const STATUS = {
  going: 'Going',
  interested: 'Interested',
  not_going: "Can't go"
};

const attendeeName = (rsvp) =>
  personName(rsvp)
  || rsvp.email
  || 'Guest';

const people = (count) => `${count} ${count === 1 ? 'person' : 'people'}`;

// What the roster says about somebody's membership.
//
// This used to be "not a walk-in, therefore a member", which labelled every
// RSVP a member. Hosts read that badge at the door, so a guest arrived already
// tagged as somebody who had paid.
//
// `isCurrentMember` is the app-wide rule - derived state first, the `is_member`
// cache only as a fallback, merged shells excluded - and MembershipChip renders
// nothing for a non-member, which is how member rows stay meaningful elsewhere.
const membershipState = (rsvp) =>
  (isCurrentMember(rsvp) ? (rsvp.membership_state || 'ACTIVE') : 'NEVER');

const sentLine = (update) => {
  const when = new Date(update.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return update.recipient_count != null
    ? `${when}, sent to ${people(update.recipient_count)}`
    : `Sent ${when}`;
};

const checkInLine = (rsvp) => {
  if (!rsvp.checked_in_at) return 'Checked in';
  const at = new Date(rsvp.checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `Checked in ${at}`;
};

/**
 * The host's workspace for one event: who is coming, who is running it, and what has been
 * said to them. These used to be admin sections stacked under the member's view of the
 * event, so a host scrolled past their own RSVP buttons to reach the roster and a member
 * scrolled past a roster they had no business seeing.
 */
function EventManage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [tab, setTab] = useState('attendees');
  const [actionsOpen, setActionsOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [qrLink, setQrLink] = useState(null);

  const {
    event, rsvps, loading, isHost, error,
    walkinData, setWalkinData,
    updateMessage, setUpdateMessage,
    sendingUpdate,
    eventUpdates,
    users,
    confirmDialog, setConfirmDialog, confirmDialogSubmitting,
    handleCheckIn,
    handleUndoCheckIn,
    handleDeleteRsvp,
    handleAssignHost,
    handleRemoveHost,
    handleWalkinSubmit,
    handleDeleteEvent,
    handleSendUpdate
  } = useEventDetails(eventId, currentUser, isAdmin, navigate);

  const counts = useMemo(() => ({
    going: rsvps.filter((rsvp) => rsvp.status === 'going').length,
    interested: rsvps.filter((rsvp) => rsvp.status === 'interested').length,
    // A plain count, not "3 of 12": walk-ins and interested people can both check in, so
    // the two numbers share no denominator and a fraction reads as nonsense.
    checkedIn: rsvps.filter((rsvp) => rsvp.checked_in).length
  }), [rsvps]);

  const audience = counts.going + counts.interested;

  // The server requires a real email, so the Add button waits for one rather than letting
  // the host submit and collect a 400.
  const canAddWalkin = Boolean(
    walkinData.firstName?.trim() && /\S+@\S+\.\S+/.test(walkinData.email || '')
  );

  // Board game and video game nights are the ones with a discounted rate to collect
  const isGameNight = event?.event_category === 'board_game' || event?.event_category === 'video_game';

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      showToast(`${link.name} link copied`, 'success');
    } catch (err) {
      showToast('Could not copy that link', 'error');
    }
  };

  // The public RSVP page, not this event's detail page. It is the one URL a stranger can
  // open without an account, and the one nginx hands to link-preview crawlers so it
  // unfurls with the series artwork rather than a generic club card (docs/LINK_PREVIEWS.md).
  const rsvpLink = { name: 'RSVP', url: `${window.location.origin}/events/${eventId}/rsvp` };

  const shareRsvpLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: event.title, url: rsvpLink.url });
        return;
      } catch (err) {
        // Dismissing the share sheet is a choice, not a failure, so it must not fall
        // through to the clipboard and claim it copied something.
        if (err?.name === 'AbortError') return;
      }
    }
    copyLink(rsvpLink);
  };

  if (loading) return <div className="loading">Loading event...</div>;
  // Only a missing event is a dead end. Any other error is reported in place, because
  // wiping the screen on a failed walk-in loses the roster the host is working from.
  if (!event) return <div className="error">Event not found</div>;

  if (!isAdmin && !isHost) {
    return (
      <div className="evm">
        <div className="evm-head">
          <Link className="evm-back" to={`/events/${eventId}`}>
            <DSIcon name="arrowLeft" size="sm" />
            Event page
          </Link>
        </div>
        <div className="evm-panel">
          <p className="evm-note">Only a host of this event or an admin can manage it.</p>
        </div>
      </div>
    );
  }

  const sorted = [...rsvps].sort((a, b) => {
    const order = { going: 0, interested: 1, not_going: 2 };
    const byStatus = (order[a.status] ?? 3) - (order[b.status] ?? 3);
    return byStatus !== 0 ? byStatus : attendeeName(a).localeCompare(attendeeName(b));
  });

  return (
    <div className="evm">
      <div className="evm-head">
        <div className="evm-head__row">
          <Link className="evm-back" to={`/events/${eventId}`}>
            <DSIcon name="arrowLeft" size="sm" />
            Event page
          </Link>

          {isAdmin && (
            <button type="button" className="evm-actions" onClick={() => setActionsOpen(!actionsOpen)}>
              Actions
              <DSIcon name="chevronDown" size="sm" />
            </button>
          )}

          {actionsOpen && (
            <div className="evm-menu">
              <button
                type="button"
                className="evm-menu__item"
                onClick={() => { setActionsOpen(false); navigate(`/events/${eventId}?edit=1`); }}
              >
                <DSIcon name="edit" size="sm" />
                Edit event
              </button>
              <button
                type="button"
                className="evm-menu__item evm-menu__item--danger"
                onClick={() => { setActionsOpen(false); handleDeleteEvent(); }}
              >
                <DSIcon name="trash" size="sm" />
                Delete event
              </button>
            </div>
          )}
        </div>

        <h2 className="evm-title">{event.title}</h2>
        <p className="evm-when">
          {new Date(event.event_date).toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
          })}
          {' at '}
          {new Date(event.event_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </p>
      </div>

      {/* The tools below stay live so a host can still reach the list, but nobody should
          mistake this for a night that is going ahead. */}
      <EventCancelledBanner
        event={event}
        note="RSVPs are closed. An admin can reinstate it from Series dates if it is back on."
      />

      {error && <p className="evm-error" role="alert">{error}</p>}

      <div className="evm-tabs" role="tablist" aria-label="Manage sections">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={`evm-tab${tab === item.key ? ' is-active' : ''}`}
            onClick={() => { setTab(item.key); setActionsOpen(false); }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'attendees' && (
        <>
          <div className="evm-stats">
            <div className="evm-stat">
              <span className="evm-stat__value">{counts.going}</span>
              <span className="evm-stat__label">Going</span>
            </div>
            <div className="evm-stat">
              <span className="evm-stat__value">{counts.interested}</span>
              <span className="evm-stat__label">Interested</span>
            </div>
            <div className="evm-stat">
              <span className="evm-stat__value">{counts.checkedIn}</span>
              <span className="evm-stat__label">Checked in</span>
            </div>
          </div>

          {/* A host's first job after their event exists is telling people about it. This
              sits above the roster because filling the roster is what it is for. It went
              missing when the member view and this workspace split apart: the only Copy
              RSVP Link button was left on a branch of EventDetails that a normal event no
              longer renders. */}
          <DSCard
            frame="bars"
            headerTone="strong"
            density="compact"
            flush
            borderVariant="accent"
            title="Share this event"
            subtitle="Anyone can open this page and RSVP without an account. Posted in Discord or sent in a text, it shows the event's artwork and details."
            className="evm-share"
          >
            <div className="evm-share__row">
              {/* Shown in full, not truncated to an ellipsis: a host who cannot use the
                  clipboard on their device needs to be able to read it off the screen. */}
              <span className="evm-share__url">{rsvpLink.url}</span>
              {/* Share is primary because on a phone it opens the sheet that puts the link
                  straight into Discord or a group text, which is the actual errand. */}
              <button type="button" className="evm-share__go" onClick={shareRsvpLink}>
                <DSIcon name="link" size="sm" />
                Share
              </button>
              <button
                type="button"
                className="evm-share__icon"
                aria-label="Copy RSVP link"
                onClick={() => copyLink(rsvpLink)}
              >
                <DSIcon name="copy" size="sm" />
              </button>
              <button
                type="button"
                className="evm-share__icon"
                aria-label="Show RSVP QR code"
                onClick={() => setQrLink({ name: 'RSVP link', url: rsvpLink.url })}
              >
                <DSIcon name="qr" size="sm" />
              </button>
            </div>
          </DSCard>

          {isGameNight && (
            /* A header strip over a list of rows, which is what this is. The passes were
               cards inside a card, and the two sets of padding and borders squeezed the
               names into three lines apiece. Flush rows reach the card edges and get that
               width back. */
            <DSCard
              frame="bars"
              headerTone="strong"
              density="compact"
              flush
              borderVariant="accent"
              title="Guest payment"
              subtitle="Show the QR or send the link and the guest pays by card for the game night rate instead of the full day pass."
              className="evm-pay"
            >
              {PASSES.map((pass) => (
                <div key={pass.id} className="evm-pass">
                  <span className="evm-pass__who">
                    <span className="evm-pass__name">{pass.name}</span>
                    <span className="evm-pass__desc">{pass.note}</span>
                  </span>
                  {/* The QR is the at-the-door action, so it is the primary button and the
                      link is a small icon beside it, not an equal choice. */}
                  <button type="button" className="evm-pass__qr" onClick={() => setQrLink(pass)}>
                    <DSIcon name="card" size="sm" />
                    Show QR
                  </button>
                  <button
                    type="button"
                    className="evm-pass__copy"
                    aria-label={`Copy ${pass.name} link`}
                    onClick={() => copyLink(pass)}
                  >
                    <DSIcon name="copy" size="sm" />
                  </button>
                </div>
              ))}
            </DSCard>
          )}

          <section className="evm-section">
            <div className="evm-section__head">
              <h3 className="evm-section__title">Attendees</h3>
              <span className="evm-section__count">{people(rsvps.length)}</span>
              {/* At the door you are checking people in and adding whoever turned up, so
                  this belongs with the roster rather than in an event-level menu. */}
              <button type="button" className="evm-walkin" onClick={() => setWalkinOpen(true)}>
                <DSIcon name="plus" size="sm" />
                Add walk-in
              </button>
            </div>

            {sorted.length === 0 ? (
              <div className="evm-empty">
                <p className="evm-empty__title">No RSVPs yet</p>
                <p className="evm-empty__note">Attendees appear here as they register.</p>
              </div>
            ) : (
              <div className="evm-list">
                {sorted.map((rsvp) => (
                  <div key={rsvp.id} className="evm-att">
                    <div className="evm-att__top">
                      <span className="evm-att__who">
                        <span className="evm-att__nameRow">
                          <span className="evm-att__name">{attendeeName(rsvp)}</span>
                          {/* Two different facts, so both can show: "Walk-in" is how they
                              got on the roster, the chip is whether they are a member. A
                              member who turned up at the door is both. */}
                          {rsvp.is_walkin && <span className="evm-tag evm-tag--walkin">Walk-in</span>}
                          <MembershipChip state={membershipState(rsvp)} size="sm" uppercase withIcon={false} />
                        </span>
                        {rsvp.email && <span className="evm-att__email">{rsvp.email}</span>}
                      </span>
                      <span className={`evm-status evm-status--${rsvp.status}`}>
                        {STATUS[rsvp.status] || rsvp.status}
                      </span>
                    </div>

                    {rsvp.message && <p className="evm-att__message">{rsvp.message}</p>}

                    <div className="evm-att__foot">
                      {rsvp.checked_in ? (
                        <>
                          <span className="evm-att__in">
                            <DSIcon name="check" size="sm" />
                            {checkInLine(rsvp)}
                          </span>
                          <button type="button" className="evm-undo" onClick={() => handleUndoCheckIn(rsvp.user_id)}>
                            Undo
                          </button>
                        </>
                      ) : (
                        <button type="button" className="evm-checkin" onClick={() => handleCheckIn(rsvp.user_id)}>
                          Check in
                        </button>
                      )}
                      <button
                        type="button"
                        className="evm-trash"
                        aria-label={`Remove ${attendeeName(rsvp)}`}
                        onClick={() => setRemoveTarget(rsvp)}
                      >
                        <DSIcon name="trash" size="sm" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {tab === 'hosts' && (
        <section className="evm-panel">
          <h3 className="evm-section__title">Hosts for this event</h3>
          <div className="evm-list">
            {(event.hosts || []).map((host) => (
              <div key={host.id} className="evm-host">
                <span className="evm-host__who">
                  <span className="evm-host__name">
                    {host.preferred_name || host.first_name} {host.last_name}
                  </span>
                  {host.email && <span className="evm-host__email">{host.email}</span>}
                </span>
                {isAdmin && (
                  <button type="button" className="evm-remove" onClick={() => handleRemoveHost(host.id)}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {(event.hosts || []).length === 0 && (
              <p className="evm-note">No hosts assigned yet.</p>
            )}
          </div>

          {isAdmin ? (
            <button type="button" className="evm-assign" onClick={() => setAssignOpen(true)}>
              <DSIcon name="plus" size="sm" />
              Assign host
            </button>
          ) : (
            <p className="evm-note">Only an admin can assign or remove hosts.</p>
          )}
        </section>
      )}

      {tab === 'updates' && (
        <section className="evm-section">
          <div className="evm-compose">
            <h3 className="evm-section__title">Send an update</h3>
            <p className="evm-compose__audience">
              Goes to everyone going or interested: {people(audience)}. Not-going RSVPs are excluded.
            </p>
            <textarea
              className="evm-compose__textarea"
              rows="3"
              placeholder="What should attendees know?"
              value={updateMessage}
              onChange={(e) => setUpdateMessage(e.target.value)}
            />
            <button
              type="button"
              className="evm-send"
              disabled={!updateMessage.trim() || sendingUpdate || audience === 0}
              onClick={handleSendUpdate}
            >
              {sendingUpdate
                ? 'Sending...'
                : updateMessage.trim()
                  ? `Send to ${people(audience)}`
                  : 'Send update'}
            </button>
          </div>

          <div className="evm-sentHead">
            <h3 className="evm-section__title">Sent</h3>
            <span className="evm-sentHead__rule" />
          </div>

          <div className="evm-sent">
            {eventUpdates.length > 0 ? eventUpdates.map((update) => (
              <div key={update.id} className="evm-sent__item">
                <p className="evm-sent__meta">{sentLine(update)}</p>
                <p className="evm-sent__body">{update.message}</p>
              </div>
            )) : (
              <p className="evm-note">Nothing sent yet.</p>
            )}
          </div>
        </section>
      )}

      {assignOpen && (
        <DSSheet
          title="Assign a host"
          onClose={() => setAssignOpen(false)}
          footer={(
            <DSButton appearance="outline" tone="neutral" onClick={() => setAssignOpen(false)}>
              Cancel
            </DSButton>
          )}
        >
          {/* Inline, not the popover: a dropdown inside a content-height sheet is clipped
              by the sheet, which left one and a half names visible and nothing to scroll. */}
          <HostPicker
            id="event-assign-host"
            inline
            label=""
            hosts={users}
            excludeIds={(event.hosts || []).map((host) => host.id)}
            value=""
            onChange={(userId) => {
              if (userId) {
                handleAssignHost(userId);
                setAssignOpen(false);
              }
            }}
            emptyMessage="Nobody matches that"
          />
        </DSSheet>
      )}

      {walkinOpen && (
        <DSSheet
          title="Add a walk-in"
          onClose={() => setWalkinOpen(false)}
          footer={(
            <>
              <DSButton appearance="outline" tone="neutral" onClick={() => setWalkinOpen(false)}>
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone="primary"
                disabled={!canAddWalkin}
                onClick={async () => {
                  const added = await handleWalkinSubmit();
                  if (added) {
                    setWalkinOpen(false);
                  }
                }}
              >
                Add
              </DSButton>
            </>
          )}
        >
          <p className="evm-note">
            Somebody who turned up without RSVPing. They are added as going, and an account is
            created for them from the email so they can be checked in and get event updates.
          </p>
          <DSInput
            label="First name *"
            value={walkinData.firstName || ''}
            onChange={(e) => setWalkinData({ ...walkinData, firstName: e.target.value })}
          />
          <DSInput
            label="Last name"
            value={walkinData.lastName || ''}
            onChange={(e) => setWalkinData({ ...walkinData, lastName: e.target.value })}
          />
          {/* Not optional: the server builds the walk-in an account keyed on this address,
              and rejects the whole request without it. The field used to say otherwise. */}
          <DSInput
            label="Email *"
            type="email"
            inputMode="email"
            value={walkinData.email || ''}
            onChange={(e) => setWalkinData({ ...walkinData, email: e.target.value })}
            helpText="Needed to create their account and check them in."
          />
        </DSSheet>
      )}

      {qrLink && (
        <DSModal title={qrLink.name} size="sm" onClose={() => setQrLink(null)}>
          <div className="evm-qr">
            {/* On a white plate: a dark surface behind a QR is what stops a phone camera
                resolving it, and this one is meant to be scanned across a table. */}
            <div className="evm-qr__code">
              <QRCode value={qrLink.url} size={216} level="H" style={{ maxWidth: '100%', height: 'auto' }} />
            </div>
            <p className="evm-qr__url">{qrLink.url}</p>
            <p className="evm-qr__hint">Guest scans, pays by card, and you check them in.</p>
          </div>
        </DSModal>
      )}

      {removeTarget && (
        <ConfirmModal
          title={`Remove ${attendeeName(removeTarget)}?`}
          message="This takes them off the list for this event. They can RSVP again themselves."
          confirmLabel="Remove"
          danger
          onConfirm={async () => {
            await handleDeleteRsvp(removeTarget.user_id, attendeeName(removeTarget));
            setRemoveTarget(null);
          }}
          onCancel={() => setRemoveTarget(null)}
        />
      )}

      {confirmDialog && (
        <DSModal title={confirmDialog.title} size="md" onClose={() => setConfirmDialog(null)}>
          <p className="evm-note">{confirmDialog.message}</p>
          <div className="form-actions">
            <DSButton
              appearance="outline"
              tone="neutral"
              onClick={() => setConfirmDialog(null)}
              disabled={confirmDialogSubmitting}
            >
              Cancel
            </DSButton>
            <DSButton
              appearance="solid"
              tone={confirmDialog.danger ? 'danger' : 'primary'}
              onClick={confirmDialog.onConfirm}
              disabled={confirmDialogSubmitting}
            >
              {confirmDialogSubmitting ? 'Working...' : confirmDialog.confirmLabel}
            </DSButton>
          </div>
        </DSModal>
      )}
    </div>
  );
}

export default EventManage;
